/**
 * Ein Standbild der Bühne aufnehmen — für das Kachelbild in „Meine Scrollys".
 *
 * FRISCH MALEN UND IM SELBEN TASK AUSLESEN: Ohne `preserveDrawingBuffer` ist
 * der WebGL-Puffer nach dem Compositing leer; ein späteres `toDataURL` liefert
 * nur Schwarz. Deshalb erst `animate()`, dann sofort abzeichnen.
 *
 * Text- und HTML-Overlays (CSS3D, Infografik-DOM) liegen NICHT im Canvas — die
 * Aufnahme zeigt die WebGL-Bühne. Für eine Kachel reicht das; die HTML-Fenster
 * würden dort ohnehin zu Streifen zusammenfallen.
 *
 * Lag bis 05.08.2026 im Pro-Editor-Adapter. Der Template-Editor bekommt
 * denselben Knopf („Aktuelle Ansicht aufnehmen", Jans Vorgabe), und zwei
 * Fassungen derselben Aufnahme wären zwei Gelegenheiten, dass die Kacheln
 * unterschiedlich aussehen.
 */

import { resolveSzenenGrundfarbe } from "@scrolly/post-types";
import { WebGLAssets } from "../Player/webglAssets";
import { setzeEigenbewegungAufnahme } from "../Player/eigenbewegungLaufzeit";

/** Bildformat einer Aufnahme (s. captureRendererFrame). */
export interface AufnahmeAusgabe {
    typ?: "image/jpeg" | "image/png";
    qualitaet?: number;
}

interface RendererLike {
    webGLRenderer?: { domElement?: HTMLCanvasElement } | null;
    props?: {
        slidePlayer?: { timer?: number };
        presentation?: { presentationOptions?: unknown } | null;
    };
    animate?: (timer: number) => void;
    webGlScene?: {
        traverse?: (fn: (obj: unknown) => void) => void;
    } | null;
    /** Host der HTML-Ebenen (SVG-/Infografik-Overlays) neben dem Canvas. */
    cssRendererDom?: HTMLElement | null;
}

/**
 * Sind alle Troika-Textebenen der Szene fertig aufgebaut? Der SDF-Font-Atlas
 * laedt asynchron — direkt nach dem Boot fehlt frisch platzierten Texten das
 * `textRenderInfo`, und eine Aufnahme in dem Moment zeigte die Szene OHNE
 * Text (Jans Kachel-Fund 31.08.2026). Aufrufer koennen bei `false` spaeter
 * erneut aufnehmen.
 */
export function rendererTextBereit(renderer: RendererLike | null | undefined): boolean {
    let bereit = true;
    try {
        renderer?.webGlScene?.traverse?.((obj) => {
            const o = obj as { constructor?: { name?: string }; textRenderInfo?: unknown };
            if (o?.constructor?.name === "Text" && !o.textRenderInfo) bereit = false;
        });
    } catch {
        /* Szene nicht lesbar — nicht blockieren */
    }
    return bereit;
}

/**
 * Womit werden die DURCHSICHTIGEN Stellen der Aufnahme hinterlegt?
 *
 * Im Reader-Modus (Template-Editor, Viewer) ist der WebGL-Canvas durchsichtig;
 * die Hintergrundfarbe der Szene liegt als CSS DAHINTER (VisualStoryPlayer,
 * `scrollyBg`). Die Aufnahme liest nur den Canvas — sie sah die Farbe nie und
 * fuellte fest mit Schwarz: die Kachel eines Templates ohne (sichtbares)
 * Hintergrundbild zeigte Schwarz statt der eingestellten Hintergrundfarbe
 * (Jans Fund 21.09.2026). Seit 23.10.2026 folgt die Aufnahme genau der
 * Bühne: Szenenfarbe, sonst Box-Hintergrund, sonst Schwarz (Pro-Editor:
 * immer die Grundfarbe).
 */
export function aufnahmeHintergrund(
    presentationOptions: unknown,
    /** Pro-Editor (WebGLAssets.editorMode): die Bühne zeigt dort immer die Grundfarbe */
    imEditor: boolean = WebGLAssets.editorMode,
): string {
    const grund = resolveSzenenGrundfarbe(presentationOptions);
    // DIESELBE Regel wie die Bühne (VisualStoryPlayer, scrollyBg — Jans Fund
    // 23.10.2026: Bühne weiß, Kacheln links schwarz): Pro-Editor immer die
    // Grundfarbe; sonst die Szenenfarbe, dann der Box-Hintergrund
    // (slideOptions.backgroundColor, im Reader `readerBg`). Nur wenn beides
    // fehlt, bleibt es beim bisherigen Schwarz.
    return imEditor || grund.quelle !== "standard" ? grund.css : "#000";
}

export function captureRendererFrame(
    renderer: RendererLike | null | undefined,
    breite = 640,
    /** Aufnahme an einem EXPLIZITEN Timer (Jans Kachel-Screenshots
     *  31.08.2026): Der Vorschau-Timer hinkt beim Slide-Wechsel hinterher —
     *  Ebenen mit kurzem Lebenszeit-Fenster (Troika-Text!) fehlten dann im
     *  Bild. Mit Override wird deterministisch die SLIDE-MITTE gerendert und
     *  danach der Original-Timer wiederhergestellt (kein sichtbarer Sprung). */
    timerOverride?: number,
    /** false = Original-Timer NICHT zurueckmalen — fuer Warm-Aufnahmen in
     *  zwei Schritten: Der Troika-Text-Sync laeuft asynchron; wer nach der
     *  Aufnahme sofort zurueckspringt, macht den Aufbau bei jedem Versuch
     *  wieder kalt und erntet nie den fertigen Text (Jans Kachel-Fund,
     *  31.08.2026). Erster Aufruf haelt den Timer (restore=false), ein
     *  zweiter kurz darauf erntet und stellt zurueck (restore=true).
     *  Die Warm-Aufnahme liefert KEIN Bild (null): ihr Ergebnis wurde nie
     *  benutzt, das Auslesen des Canvas kostete aber jedes Mal 10–25 ms. */
    restore = true,
    /** Wird IM Aufnahme-Moment gerufen (nach dem Rendern am Ziel-Timer, vor
     *  dem Zuruecksprung) — fuer synchrone DOM-Schnappschuesse der
     *  HTML-Overlays (s. captureRendererFrameMitOverlays). */
    imAufnahmeMoment?: () => void,
    /** Bildformat der Aufnahme — Standard JPEG 0.8 (Kacheln). Der Export
     *  in „My Scrollys" (Jan 16.10.2026) nimmt PNG, damit das Endbild nicht
     *  zweimal verlustbehaftet gespeichert wird. */
    ausgabe?: AufnahmeAusgabe,
): string | null {
    // Stille Aufnahme: kein Video darf dabei starten, stoppen oder springen
    // (Tonfetzen, s. WebGLAssets.stillCapture). Flag gilt fuer alle
    // animate()-Aufrufe hier inklusive des Zuruecksprungs.
    const vorher = WebGLAssets.stillCapture;
    WebGLAssets.stillCapture = true;
    try {
        const canvas = renderer?.webGLRenderer?.domElement;
        if (!renderer || !canvas) return null;
        const originalTimer = Math.max(0, Number(renderer.props?.slidePlayer?.timer ?? 0) || 0);
        const fremderTimer = timerOverride !== undefined && Number.isFinite(timerOverride);
        const timer = fremderTimer ? Math.max(0, timerOverride) : originalTimer;
        // RUHEPOSE fuer Kachel-Aufnahmen (Jans Fund 02.10.2026): Ebenen mit
        // Eigenbewegung standen in jeder Aufnahme anders da — die Kacheln
        // wechselten staendig und die Buehne zuckte. An einem fremden Timer
        // wird deshalb ohne Eigenbewegung aufgenommen; die Uhr laeuft weiter.
        // „Aktuelle Ansicht aufnehmen" (ohne Override) bleibt, was man sieht.
        if (fremderTimer) setzeEigenbewegungAufnahme(true);
        renderer.animate?.(timer);
        // ZWEITER Durchlauf vor dem Auslesen (Jans Kachel-Fund 31.08.2026,
        // Pixel-Diff-Beleg): Nach einem Timer-Sprung baut der erste Render
        // Lazy-Objekte (Troika-Text!) erst auf — gemalt sind sie erst im
        // naechsten Durchlauf. Ein Render kostet wenige ms.
        renderer.animate?.(timer);
        // Ab hier wieder mit Eigenbewegung: alles, was jetzt noch gemalt
        // wird, bleibt auf der Buehne stehen.
        setzeEigenbewegungAufnahme(false);
        if (!restore) {
            // Warm-Aufnahme: der Timer bleibt gehalten, aber die Buehne soll
            // nicht fuer ein Bild in der Ruhepose stehen — einmal mit
            // Eigenbewegung nachmalen. Kein Auslesen (s. Parameter).
            if (fremderTimer) renderer.animate?.(timer);
            return null;
        }
        if (!canvas.width || !canvas.height) return null;

        const zielBreite = Math.max(1, Math.round(breite));
        const zielHoehe = Math.max(
            1,
            Math.round((zielBreite * canvas.height) / canvas.width),
        );
        const ziel = document.createElement("canvas");
        ziel.width = zielBreite;
        ziel.height = zielHoehe;
        const ctx = ziel.getContext("2d");
        if (!ctx) return null;
        // JPEG kennt kein Alpha — transparente Stellen wuerden sonst je nach
        // Browser weiss oder schwarz. Deckend hinterlegen wie die Buehne:
        // in der Farbe, die die Bühne hinter den Ebenen zeigt (s. aufnahmeHintergrund).
        ctx.fillStyle = aufnahmeHintergrund(renderer.props?.presentation?.presentationOptions);
        ctx.fillRect(0, 0, zielBreite, zielHoehe);
        ctx.drawImage(canvas, 0, 0, zielBreite, zielHoehe);
        imAufnahmeMoment?.();
        const bild = ziel.toDataURL(ausgabe?.typ ?? "image/jpeg", ausgabe?.qualitaet ?? 0.8);
        // Original-Zustand zurueckmalen, wenn an einem fremden Timer
        // aufgenommen wurde — sonst zeigte die Buehne bis zum naechsten
        // Loop-Tick kurz den Aufnahme-Moment.
        if (restore && timerOverride !== undefined && timer !== originalTimer) {
            renderer.animate?.(originalTimer);
        }
        return bild;
    } catch {
        return null;
    } finally {
        setzeEigenbewegungAufnahme(false);
        WebGLAssets.stillCapture = vorher;
    }
}

/**
 * Aufnahme MIT den HTML-Ebenen (Jans Kachel-Fund 12.10.2026: flache SVGs —
 * Logo, Infografik — rendern als DOM-Overlay NEBEN dem WebGL-Canvas und
 * fehlten in den Slide-Kacheln; dieselbe Luecke wie beim Scene-Foto #1393).
 *
 * Ablauf: Die synchrone Aufnahme oben rendert am (fremden) Timer — animate()
 * zieht dabei auch die HTML-Overlays nach. Der Host wird deshalb IM
 * Aufnahme-Moment synchron zu XML serialisiert (danach stellt restore die
 * Buehne zurueck) und anschliessend asynchron als SVG-foreignObject uebers
 * WebGL-Bild gelegt. Scheitert das Rastern (externe Verweise, Taint), kommt
 * wenigstens das reine WebGL-Bild zurueck — nie weniger als bisher.
 */
export async function captureRendererFrameMitOverlays(
    renderer: RendererLike | null | undefined,
    breite = 640,
    timerOverride?: number,
    restore = true,
    ausgabe?: AufnahmeAusgabe,
): Promise<string | null> {
    let xml: string | null = null;
    let hostBreite = 0;
    let hostHoehe = 0;
    const nachAnimate = () => {
        try {
            const host = renderer?.cssRendererDom;
            if (host && host.innerHTML.trim()) {
                const b = host.getBoundingClientRect();
                if (b.width > 0 && b.height > 0) {
                    xml = new XMLSerializer().serializeToString(host);
                    hostBreite = Math.round(b.width);
                    hostHoehe = Math.round(b.height);
                }
            }
        } catch {
            xml = null;
        }
    };
    const webgl = captureRendererFrame(renderer, breite, timerOverride, restore, nachAnimate, ausgabe);
    if (!webgl || !xml) return webgl;
    try {
        const grundBild = new Image();
        await new Promise<void>((ok, nein) => {
            grundBild.onload = () => ok();
            grundBild.onerror = () => nein(new Error("webgl-bild nicht lesbar"));
            grundBild.src = webgl;
        });
        const ziel = document.createElement("canvas");
        ziel.width = grundBild.width;
        ziel.height = grundBild.height;
        const ctx = ziel.getContext("2d");
        if (!ctx) return webgl;
        ctx.drawImage(grundBild, 0, 0);
        // Das SVG rastert den Host in SEINER Groesse (CSS-Pixel der Buehne)
        // und wird erst beim Zeichnen auf die Kachel skaliert — sonst waere
        // das Overlay gegen das verkleinerte WebGL-Bild verschoben.
        const svgText =
            `<svg xmlns="http://www.w3.org/2000/svg" width="${hostBreite}" height="${hostHoehe}">` +
            `<foreignObject width="100%" height="100%">${xml}</foreignObject></svg>`;
        const overlay = new Image();
        await new Promise<void>((ok, nein) => {
            overlay.onload = () => ok();
            overlay.onerror = () => nein(new Error("html-ebene nicht rasterbar"));
            overlay.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svgText);
        });
        ctx.drawImage(overlay, 0, 0, ziel.width, ziel.height);
        return ziel.toDataURL(ausgabe?.typ ?? "image/jpeg", ausgabe?.qualitaet ?? 0.8);
    } catch {
        // Taint oder Rasterfehler: WebGL-Bild reicht.
        return webgl;
    }
}
